import { ICandidateAssessment } from "@/modules/assessments/assessments.types";
import { ITextLocation } from "@/modules/assessments/scenes/CreateAssessment/assessment.interface";
import CandidateTestElement from "@/modules/candidate/assessment/CandidateAssessment/components/CandidateTestElement/CandidateTestElement";
import { CandidateAssessmentViewTypes } from "@/modules/candidate/assessment/CandidateAssessment/CandidateAssessment";
import { getTestsProps } from "@/modules/candidate/utils/candidateAssessment.utils";
import { ILocalizedTestElement, LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";

import { ITestChoice } from "@/shared/components/candidate/scenes/TestElement/TestElement";
import WelcomeElement from "@/shared/components/candidate/scenes/WelcomeElement/WelcomeElement";
import AssessmentIntroView from "../AssessmentIntroView/AssessmentIntroView";
import { useLocation, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { getRouteWithSearchParameter } from "@/shared/utils/route/route.utils";
import { Button } from "@/ui-components/commonComponents/Button/Button";
import { CandidateFeedbackSurvey, FeedbackQuestionsResponse } from "@/modules/candidate/feedback";
import { Loading } from "@/shared/components/Loading/Loading";
import { ContentGatedFooter } from "../ContentGatedFooter/ContentGatedFooter";

interface IContentView {
  assessment: ICandidateAssessment | undefined;
  textLocations: ITextLocation[];
  viewType: CandidateAssessmentViewTypes;
  currentPage: number;
  isLoadingTestInstructions: boolean;
  currentAnswers: ITestChoice[];
  buttonTitle: string;
  setIsOpenedInstructions: (value: boolean) => void;
  handleChoiceSelect: (value: ITestChoice) => void;
  handleSubmitClick: () => Promise<void>;
  timerDisabled?: boolean;
  questionsList: ILocalizedTestElement[][];
  hasAcceptedTerms: boolean;
  inviteId?: number;
  testId?: number;
  isOnLandingPage: boolean;
  eligibleForLandingPage: boolean;
  feedbackData?: FeedbackQuestionsResponse | null;
  isFeedbackLoading?: boolean;
  surveyCompleted?: boolean;
  onSurveyComplete?: () => void;
  demographicsSubmittedAt?: string | null;
  employeeId?: string | null;
  locale?: LanguagesIsoEnum | null;
  hasStarted?: boolean;
  onLocaleConfirmed?: (locale: LanguagesIsoEnum) => void;
  onLocaleSelected?: (locale: LanguagesIsoEnum) => void;
  onIntakeComplete?: () => void;
  // True while what's on screen isn't the requested locale's content — including after a failed
  // re-fetch, which is why the retry has to travel with it.
  isContentStale?: boolean;
  hasContentError?: boolean;
  onRetryContent: () => void;
}

export default function ContentView({
  assessment,
  textLocations,
  viewType,
  currentPage,
  currentAnswers,
  isLoadingTestInstructions,
  buttonTitle,
  setIsOpenedInstructions,
  handleChoiceSelect,
  handleSubmitClick,
  timerDisabled = false,
  questionsList,
  hasAcceptedTerms,
  inviteId,
  testId,
  isOnLandingPage = false,
  eligibleForLandingPage = false,
  feedbackData,
  isFeedbackLoading = false,
  surveyCompleted = false,
  onSurveyComplete,
  demographicsSubmittedAt,
  employeeId,
  locale,
  hasStarted = false,
  onLocaleConfirmed,
  onLocaleSelected,
  onIntakeComplete,
  isContentStale = false,
  hasContentError = false,
  onRetryContent,
}: IContentView) {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();

  const { title, leftTime, introText, landingText, outroText, maxTestIndexInModule } = getTestsProps({
    assessment,
    textLocations,
    intl,
  });

  if (isOnLandingPage) {
    return (
      <WelcomeElement
        type={viewType}
        title={title}
        description={landingText}
        leftTime={leftTime}
        timerDisabled={timerDisabled}
        footer={
          <ContentGatedFooter
            blocked={isContentStale}
            isStale={isContentStale}
            hasError={hasContentError}
            onRetry={onRetryContent}
          >
            {({ isLoading, disabled }) => (
              <Button onClick={handleSubmitClick} isLoading={isLoading} disabled={disabled}>
                {buttonTitle}
              </Button>
            )}
          </ContentGatedFooter>
        }
      />
    );
  }

  const onSubmitSuccess = eligibleForLandingPage
    ? async () => {
        // The invite in memory still predates the intake save, so the landing gate needs to hear about
        // the submit directly to let this navigation through.
        onIntakeComplete?.();
        navigate(getRouteWithSearchParameter(location.pathname, "page", "landing"));
      }
    : handleSubmitClick;

  if (questionsList && maxTestIndexInModule) {
    // Test questions view
    if (questionsList[currentPage - 1]) {
      return (
        <CandidateTestElement
          currentPage={currentPage}
          isLoadingTestInstructions={isLoadingTestInstructions}
          currentAnswers={currentAnswers}
          setIsOpenedInstructions={setIsOpenedInstructions}
          handleChoiceSelect={handleChoiceSelect}
          timerDisabled={timerDisabled}
        />
      );
    }

    // Start/Intro view
    if (currentPage === 0) {
      return (
        <AssessmentIntroView
          viewType={viewType}
          title={title}
          introText={introText}
          leftTime={leftTime}
          timerDisabled={timerDisabled}
          buttonTitle={buttonTitle}
          onSubmit={onSubmitSuccess}
          hasAcceptedTerms={hasAcceptedTerms}
          inviteId={inviteId}
          testId={testId}
          demographicsEnabled={assessment?.demographicsEnabled ?? false}
          employeeIdEnabled={assessment?.employeeIdEnabled ?? false}
          requiresIdentityAttestation={!assessment?.isSurvey}
          demographicsSubmittedAt={demographicsSubmittedAt}
          employeeId={employeeId}
          locale={locale}
          hasStarted={hasStarted}
          onLocaleConfirmed={onLocaleConfirmed}
          onLocaleSelected={onLocaleSelected}
          isContentStale={isContentStale}
          hasContentError={hasContentError}
          onRetryContent={onRetryContent}
        />
      );
    }

    // Finish view - check for feedback survey
    // Render loading while feedback data is loading to prevent outro flash
    if (isFeedbackLoading) {
      return <Loading />;
    }

    const hasFeedbackSurvey =
      feedbackData?.feedbackEnabled && feedbackData.questions && feedbackData.questions.length > 0;

    // Show survey if feedback is enabled and survey not yet completed
    if (hasFeedbackSurvey && !feedbackData.hasSubmittedFeedback && !surveyCompleted && inviteId && onSurveyComplete) {
      return (
        <CandidateFeedbackSurvey questions={feedbackData.questions} inviteId={inviteId} onComplete={onSurveyComplete} />
      );
    }

    // Show outro (either feedback not enabled, or survey has been completed/skipped)
    return <WelcomeElement type={viewType} description={outroText} />;
  }

  return null;
}
